import { lazy, Suspense } from 'react';
import type { ComponentType, ReactElement } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useParams } from 'react-router-dom';
import App from '../components/app/App';
import { Loader } from '../components/common/loader/Loader';
import ProtectedRoute from './ProtectedRoute';

const lazyNamed = (factory: () => Promise<Record<string, unknown>>, exportName: string) =>
  lazy(async () => {
    const module = await factory();
    return {
      default: module[exportName] as ComponentType<any>,
    };
  });

const suspenseElement = (element: ReactElement) => (
  <Suspense fallback={<Loader viewportCentered />}>{element}</Suspense>
);

const LegacyDrawerRedirect = ({ entityType }: { entityType: 'item' | 'page' }) => {
  const { id } = useParams();
  const basePath = entityType === 'item' ? '/designer/drawer-items' : '/designer/drawer-menus';

  return <Navigate to={id ? `${basePath}/${encodeURIComponent(id)}` : basePath} replace />;
};

const LegacyThemeRedirect = () => {
  const { id } = useParams();
  return (
    <Navigate
      to={id ? `/designer/themes/${encodeURIComponent(id)}` : '/designer/themes'}
      replace
    />
  );
};

const Resources = lazyNamed(() => import('../components/pages/resources/Resources'), 'Resources');
const SchemaEditView = lazy(() => import('../components/pages/resources/SchemaEditView'));
const BrandingPage = lazyNamed(
  () => import('../components/pages/branding/BrandingPage'),
  'BrandingPage',
);
const SchemasPage = lazyNamed(
  () => import('../components/pages/content/schemas/SchemasPage'),
  'SchemasPage',
);
const BrandingEditPage = lazyNamed(
  () => import('../components/pages/branding/edit/BrandingEditPage'),
  'BrandingEditPage',
);
const ThemesPage = lazyNamed(
  () => import('../components/pages/themes/ThemesPage'),
  'ThemesPage',
);
const ThemeEditPage = lazyNamed(
  () => import('../components/pages/themes/edit/ThemeEditPage'),
  'ThemeEditPage',
);
const ProcessDefinitionsPage = lazyNamed(
  () => import('../components/pages/content/processDefinition/ProcessDefinitionsPage'),
  'ProcessDefinitionsPage',
);
const DocumentTypesPage = lazyNamed(
  () => import('../components/pages/content/documentTypes/DocumentTypesPage'),
  'DocumentTypesPage',
);
const FacetsPage = lazyNamed(
  () => import('../components/pages/content/facets/FacetsPage'),
  'FacetsPage',
);
const DocumentTypePage = lazyNamed(
  () => import('../components/pages/content/documentTypes/DocumentTypePage'),
  'DocumentTypePage',
);
const LifeCyclesPage = lazyNamed(
  () => import('../components/pages/content/life-cycles/LifeCyclesPage'),
  'LifeCyclesPage',
);
const LifeCyclePage = lazy(() => import('../components/pages/content/life-cycles/LifeCyclePage'));
const PackagesPage = lazyNamed(
  () => import('../components/pages/packages/PackagesPage'),
  'PackagesPage',
);
const PackagePage = lazy(() => import('../components/pages/packages/PackagePage'));
const BillingPage = lazy(() => import('../components/pages/billing/BillingPage'));
const AuditPage = lazy(() => import('../components/pages/audit/AuditPage'));
const ProfilePage = lazy(() => import('../components/pages/profile/ProfilePage'));
const ApplicationInformationPage = lazyNamed(
  () => import('../components/pages/applicationInformation/ApplicationInformationPage'),
  'ApplicationInformationPage',
);
const ApplicationDefinitionPage = lazyNamed(
  () => import('../components/pages/applicationDefinition/ApplicationDefinitionPage'),
  'ApplicationDefinitionPage',
);
const BranchManagementPage = lazyNamed(
  () => import('../components/pages/branchManagement/BranchManagementPage'),
  'BranchManagementPage',
);
const SchemaPage = lazy(() => import('../components/pages/content/schemas/SchemaPage/SchemaPage'));
const ProcessDefinitionsView = lazy(
  () => import('../components/pages/content/processDefinition/ProcessDefinitionsView'),
);
const StructureTemplatesPage = lazyNamed(
  () => import('../components/pages/content/structureTemplates/StructureTemplatesPage'),
  'StructureTemplatesPage',
);
const StructureTemplatePage = lazyNamed(
  () =>
    import('../components/pages/content/structureTemplates/StructureTemplatePage/StructureTemplatePage'),
  'StructureTemplatePage',
);
const AutomationsScriptingPage = lazyNamed(
  () => import('../components/pages/automation/automationScripting/AutomationsScriptingPage'),
  'AutomationsScriptingPage',
);
const AutomationScriptingView = lazy(
  () => import('../components/pages/automation/automationScripting/AutomationScriptingView'),
);
const AutomationsChainsPage = lazyNamed(
  () => import('../components/pages/automation/automationChains/AutomationsChainsPage'),
  'AutomationsChainsPage',
);
const AutomationChainsView = lazyNamed(
  () => import('../components/pages/automation/automationChains/AutomationChainsView'),
  'AutomationChainsView',
);
const HomePage = lazy(() => import('../components/pages/home/HomePage'));
const LoginPage = lazy(() => import('../components/pages/login/LoginPage'));
const RegisterPage = lazy(() => import('../components/pages/register/RegisterPage'));
const AcceptInvitePage = lazy(() => import('../components/pages/acceptInvite/AcceptInvitePage'));
const DesignerView = lazy(() => import('../components/pages/designer/DesignerView'));
const EntitiesList = lazy(() => import('../components/pages/designer/EntitiesList'));
const BuiltView = lazy(() => import('../components/pages/designer/BuiltView'));
const DragDropView = lazy(() => import('../components/pages/designer/DragDropView'));
const TemplatesPage = lazyNamed(
  () => import('../components/pages/templates__entity/TemplatesPage'),
  'TemplatesPage',
);
const TemplateView = lazy(() => import('../components/pages/templates__entity/TemplateView'));
const EventHandlersPage = lazyNamed(
  () => import('../components/pages/automation/eventHandlers/EventHandlersPage'),
  'EventHandlersPage',
);
const EventHandlerView = lazyNamed(
  () => import('../components/pages/automation/eventHandlers/EventHandlerView'),
  'EventHandlerView',
);
const PermissionsPage = lazyNamed(
  () => import('../components/pages/rolesAndPermissions/permissions/PermissionsPage'),
  'PermissionsPage',
);
const PermissionsView = lazy(
  () => import('../components/pages/rolesAndPermissions/permissions/PermissionsView'),
);
const VocabulariesPage = lazyNamed(
  () => import('../components/pages/vocabularies/VocabulariesPage'),
  'VocabulariesPage',
);
const VocabularyView = lazy(() => import('../components/pages/vocabularies/VocabularyView'));
const UsersAndGroupsPage = lazyNamed(
  () => import('../components/pages/rolesAndPermissions/usersAndGroups/UsersAndGroupsPage'),
  'UsersAndGroupsPage',
);
const SearchMappingPage = lazyNamed(
  () => import('../components/pages/search/searchMapping/SearchMappingPage'),
  'SearchMappingPage',
);
const SearchMappingView = lazyNamed(
  () => import('../components/pages/search/searchMapping/SearchMappingView'),
  'SearchMappingView',
);
const PageProvidersPage = lazyNamed(
  () => import('../components/pages/search/pageProviders/PageProvidersPage'),
  'PageProvidersPage',
);
const PageProvidersView = lazyNamed(
  () => import('../components/pages/search/pageProviders/PageProvidersView'),
  'PageProvidersView',
);
const DashboardView = lazyNamed(
  () => import('../components/pages/designer/dashboard/DashboardView'),
  'DashboardView',
);
const XMLExtensionsPage = lazyNamed(
  () => import('../components/pages/advancedSettings/XMLExtinsions/XMLExtensionsPage'),
  'XMLExtensionsPage',
);
const XMLExtensionsView = lazy(
  () => import('../components/pages/advancedSettings/XMLExtinsions/XMLExtensionsView'),
);
const WebServicesFilteringPage = lazyNamed(
  () =>
    import('../components/pages/advancedSettings/webServicesFiltering/WebServicesFilteringPage'),
  'WebServicesFilteringPage',
);
const FacetView = lazyNamed(
  () => import('../components/pages/content/facets/FacetView'),
  'FacetView',
);
const TranslationsPage = lazyNamed(
  () => import('../components/pages/designer/translations/TranslationsPage'),
  'TranslationsPage',
);
const TranslationsView = lazy(
  () => import('../components/pages/designer/translations/TranslationsView'),
);
const ButtonsList = lazy(() => import('../components/pages/designer/buttons/ButtonsList'));
const ButtonView = lazy(() => import('../components/pages/designer/buttons/ButtonView'));
const DrawersPage = lazyNamed(
  () => import('../components/pages/designer/drawers/DrawersPage'),
  'DrawersPage',
);
const DrawerView = lazy(() => import('../components/pages/designer/drawers/DrawerView'));
const TabsPage = lazyNamed(() => import('../components/pages/designer/tabs/TabsPage'), 'TabsPage');
const TabView = lazy(() => import('../components/pages/designer/tabs/TabView'));
const LayoutBlocksPage = lazyNamed(
  () => import('../components/pages/designer/layoutBlocks/LayoutBlocksPage'),
  'LayoutBlocksPage',
);
const UIResourcesPage = lazy(
  () => import('../components/pages/designer/resources/UIResourcesPage'),
);
const RegistriesAutomationOperationsPage = lazyNamed(
  () =>
    import('../components/pages/registries/automationOperations/RegistriesAutomationOperationsPage'),
  'RegistriesAutomationOperationsPage',
);
const RegistriesDocumentTypesPage = lazyNamed(
  () => import('../components/pages/registries/documentTypes/RegistriesDocumentTypesPage'),
  'RegistriesDocumentTypesPage',
);
const RegistriesDocumentSchemasPage = lazyNamed(
  () => import('../components/pages/registries/documentSchemas/RegistriesDocumentSchemasPage'),
  'RegistriesDocumentSchemasPage',
);
const RegistriesDocumentFacetsPage = lazyNamed(
  () => import('../components/pages/registries/documentFacets/RegistriesDocumentFacetsPage'),
  'RegistriesDocumentFacetsPage',
);
const RegistriesCoreEventsPage = lazyNamed(
  () => import('../components/pages/registries/coreEvents/RegistriesCoreEventsPage'),
  'RegistriesCoreEventsPage',
);
const RegistriesPermissionsPage = lazyNamed(
  () => import('../components/pages/registries/permissions/RegistriesPermissionsPage'),
  'RegistriesPermissionsPage',
);
const RegistriesLifeCyclesPage = lazyNamed(
  () => import('../components/pages/registries/lifeCycles/RegistriesLifeCyclesPage'),
  'RegistriesLifeCyclesPage',
);

export const routerConfig = [
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: '',
        element: suspenseElement(
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>,
        ),
        children: [
          {
            path: 'packages',
            element: suspenseElement(<PackagesPage />),
          },
          {
            path: 'packages/:id',
            element: suspenseElement(<PackagePage />),
          },
          {
            path: 'billing',
            element: suspenseElement(
              <ProtectedRoute allowedRoles={['ORG_ADMIN', 'ADMIN']}>
                <BillingPage />
              </ProtectedRoute>,
            ),
          },
          {
            path: 'audit',
            element: suspenseElement(
              <ProtectedRoute allowedRoles={['ORG_ADMIN', 'ADMIN']} redirectTo="/">
                <AuditPage />
              </ProtectedRoute>,
            ),
          },
          {
            path: 'profile',
            element: suspenseElement(<ProfilePage />),
          },
          {
            path: 'application-information',
            element: suspenseElement(<ApplicationInformationPage />),
          },
          {
            path: 'application-definition',
            element: suspenseElement(<ApplicationDefinitionPage />),
          },
          {
            path: 'branch-management',
            element: suspenseElement(<BranchManagementPage />),
          },
          {
            path: 'resources',
            element: suspenseElement(<Resources />),
          },
          {
            path: 'resources/schemas/:filename',
            element: suspenseElement(<SchemaEditView />),
          },
          {
            path: 'branding',
            element: suspenseElement(<BrandingPage />),
          },
          {
            path: 'branding/:id',
            element: suspenseElement(<BrandingEditPage />),
          },
          {
            path: 'themes',
            element: <LegacyThemeRedirect />,
          },
          {
            path: 'themes/:id',
            element: <LegacyThemeRedirect />,
          },
          {
            path: 'content/schemas',
            element: suspenseElement(<SchemasPage />),
          },
          {
            path: 'content/schemas/:featureId',
            element: suspenseElement(<SchemaPage />),
          },
          {
            path: 'content/document-types',
            element: suspenseElement(<DocumentTypesPage />),
          },
          {
            path: 'content/document-types/:id',
            element: suspenseElement(<DocumentTypePage />),
          },
          {
            path: 'content/facets',
            element: suspenseElement(<FacetsPage />),
          },
          {
            path: 'content/facets/:id',
            element: suspenseElement(<FacetView />),
          },
          {
            path: 'content/life-cycles',
            element: suspenseElement(<LifeCyclesPage />),
          },
          {
            path: 'content/life-cycles/:id',
            element: suspenseElement(<LifeCyclePage />),
          },
          {
            path: 'content/structure-templates',
            element: suspenseElement(<StructureTemplatesPage />),
          },
          {
            path: 'content/structure-templates/:id',
            element: suspenseElement(<StructureTemplatePage />),
          },
          {
            path: 'workflow/process-definitions',
            element: suspenseElement(<ProcessDefinitionsPage />),
          },
          {
            path: 'workflow/process-definitions/:id',
            element: suspenseElement(<ProcessDefinitionsView />),
          },
          {
            path: 'automation/automation-scripting',
            element: suspenseElement(<AutomationsScriptingPage />),
          },
          {
            path: 'automation/automation-scripting/:id',
            element: suspenseElement(<AutomationScriptingView />),
          },
          {
            path: 'automation/automation-chains',
            element: suspenseElement(<AutomationsChainsPage />),
          },
          {
            path: 'automation/automation-chains/:id',
            element: suspenseElement(<AutomationChainsView />),
          },
          {
            path: 'templates',
            element: suspenseElement(<TemplatesPage />),
          },
          {
            path: 'templates/:id',
            element: suspenseElement(<TemplateView />),
          },
          {
            path: 'automation/event-handlers',
            element: suspenseElement(<EventHandlersPage />),
          },
          {
            path: 'automation/event-handlers/:id',
            element: suspenseElement(<EventHandlerView />),
          },
          {
            path: 'roles-and-permissions/permissions',
            element: suspenseElement(<PermissionsPage />),
          },
          {
            path: 'roles-and-permissions/permissions/:id',
            element: suspenseElement(<PermissionsView />),
          },
          {
            path: 'vocabularies',
            element: suspenseElement(<VocabulariesPage />),
          },
          {
            path: 'vocabularies/:id',
            element: suspenseElement(<VocabularyView />),
          },
          {
            path: 'roles-and-permissions/users-and-groups',
            element: suspenseElement(<UsersAndGroupsPage />),
          },
          {
            path: 'search/search-mapping',
            element: suspenseElement(<SearchMappingPage />),
          },
          {
            path: 'search/search-mapping/:id',
            element: suspenseElement(<SearchMappingView />),
          },
          {
            path: 'search/page-providers',
            element: suspenseElement(<PageProvidersPage />),
          },
          {
            path: 'search/page-providers/:id',
            element: suspenseElement(<PageProvidersView />),
          },
          {
            path: 'advanced-settings/xml-extensions',
            element: suspenseElement(<XMLExtensionsPage />),
          },
          {
            path: 'advanced-settings/xml-extensions/:id',
            element: suspenseElement(<XMLExtensionsView />),
          },
          {
            path: 'advanced-settings/web-services-filtering',
            element: suspenseElement(<WebServicesFilteringPage />),
          },
          {
            path: 'registries/automation-operations',
            element: suspenseElement(<RegistriesAutomationOperationsPage />),
          },
          {
            path: 'registries/document-types',
            element: suspenseElement(<RegistriesDocumentTypesPage />),
          },
          {
            path: 'registries/document-schemas',
            element: suspenseElement(<RegistriesDocumentSchemasPage />),
          },
          {
            path: 'registries/document-facets',
            element: suspenseElement(<RegistriesDocumentFacetsPage />),
          },
          {
            path: 'registries/core-events',
            element: suspenseElement(<RegistriesCoreEventsPage />),
          },
          {
            path: 'registries/permissions',
            element: suspenseElement(<RegistriesPermissionsPage />),
          },
          {
            path: 'registries/life-cycles',
            element: suspenseElement(<RegistriesLifeCyclesPage />),
          },
        ],
      },
      {
        path: 'designer',
        element: suspenseElement(
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>,
        ),
        children: [
          {
            path: '',
            children: [
              {
                path: 'local-document-types',
                element: suspenseElement(<EntitiesList entity="documentTypes" />),
              },
              {
                path: 'local-document-types/:id',
                element: suspenseElement(<DesignerView constant="doctypes" />),
              },
              {
                path: 'local-document-types/:id/create',
                element: suspenseElement(<DragDropView view="doctypes" />),
              },
              {
                path: 'local-document-types/:id/edit',
                element: suspenseElement(<DragDropView view="doctypes" />),
              },
              {
                path: 'local-document-types/:id/metadata',
                element: suspenseElement(<DragDropView view="doctypes" />),
              },
              {
                path: 'local-document-types/:id/import',
                element: suspenseElement(<DragDropView view="doctypes" />),
              },
              {
                path: 'local-document-types/:id/view',
                element: suspenseElement(<DragDropView view="doctypes" />),
              },
              {
                path: 'built-in-document-types',
                element: suspenseElement(<BuiltView />),
              },
              {
                path: 'built-in-document-types/:id',
                element: suspenseElement(<DesignerView constant="builtDocTypes" />),
              },
              {
                path: 'built-in-document-types/:id/create',
                element: suspenseElement(<DragDropView view="builtDocTypes" />),
              },
              {
                path: 'built-in-document-types/:id/edit',
                element: suspenseElement(<DragDropView view="builtDocTypes" />),
              },
              {
                path: 'built-in-document-types/:id/metadata',
                element: suspenseElement(<DragDropView view="builtDocTypes" />),
              },
              {
                path: 'built-in-document-types/:id/import',
                element: suspenseElement(<DragDropView view="builtDocTypes" />),
              },
              {
                path: 'built-in-document-types/:id/view',
                element: suspenseElement(<DragDropView view="builtDocTypes" />),
              },
              {
                path: 'workflows',
                element: suspenseElement(<EntitiesList entity="workflows" />),
              },
              {
                path: 'workflows/:id',
                element: suspenseElement(<DesignerView constant="workflows" />),
              },
              {
                path: 'workflows/:id/:step',
                element: suspenseElement(<DragDropView view="workflows" />),
              },
              {
                path: 'dashboard',
                element: suspenseElement(<DashboardView />),
              },
              {
                path: 'page-providers',
                element: suspenseElement(<EntitiesList entity="pageProviders" />),
              },
              {
                path: 'page-providers/:id',
                element: suspenseElement(<DesignerView constant="pageProviders" />),
              },
              {
                path: 'page-providers/:id/form',
                element: suspenseElement(<DragDropView view="pageProviders" />),
              },
              {
                path: 'page-providers/:id/results',
                element: suspenseElement(<DragDropView view="pageProvidersResult" />),
              },
              {
                path: 'translations',
                element: suspenseElement(<TranslationsPage />),
              },
              {
                path: 'translations/:id',
                element: suspenseElement(<TranslationsView />),
              },
              {
                path: 'buttons',
                element: suspenseElement(<ButtonsList />),
              },
              {
                path: 'buttons/:id',
                element: suspenseElement(<ButtonView />),
              },
              {
                path: 'drawer-items',
                element: suspenseElement(<DrawersPage entityType="item" />),
              },
              {
                path: 'drawer-items/:id',
                element: suspenseElement(<DrawerView entityType="item" />),
              },
              {
                path: 'drawer-menus',
                element: suspenseElement(<DrawersPage entityType="page" />),
              },
              {
                path: 'drawer-menus/:id',
                element: suspenseElement(<DrawerView entityType="page" />),
              },
              {
                path: 'drawer-elements',
                element: suspenseElement(<LegacyDrawerRedirect entityType="item" />),
              },
              {
                path: 'drawer-elements/:id',
                element: suspenseElement(<LegacyDrawerRedirect entityType="item" />),
              },
              {
                path: 'drawer-menu',
                element: suspenseElement(<LegacyDrawerRedirect entityType="page" />),
              },
              {
                path: 'drawer-menu/:id',
                element: suspenseElement(<LegacyDrawerRedirect entityType="page" />),
              },
              {
                path: 'layout-blocks',
                element: suspenseElement(<LayoutBlocksPage />),
              },
              {
                path: 'tabs',
                element: suspenseElement(<TabsPage />),
              },
              {
                path: 'tabs/:id',
                element: suspenseElement(<TabView />),
              },
              {
                path: 'themes',
                element: suspenseElement(<ThemesPage />),
              },
              {
                path: 'themes/new',
                element: suspenseElement(<ThemeEditPage />),
              },
              {
                path: 'themes/:id',
                element: suspenseElement(<ThemeEditPage />),
              },
            ],
          },
          {
            path: 'resources/*',
            element: suspenseElement(<UIResourcesPage />),
          },
        ],
      },
      {
        path: 'login',
        element: suspenseElement(<LoginPage />),
      },
      {
        path: 'register',
        element: suspenseElement(<RegisterPage />),
      },
      {
        path: 'accept-invite',
        element: suspenseElement(<AcceptInvitePage />),
      },
    ],
  },
];

const router = createBrowserRouter(routerConfig);

export function Routes() {
  return <RouterProvider router={router} />;
}
